import { type FC, useState } from "react";
import {
  Box,
  Typography,
  AppBar,
  Card,
  TablePagination,
  CircularProgress,
  Button,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useContentsQuery } from "@/partner/entities/contents";
import { ContentTypeType } from "@/types/graphql/partner.generated";
import { useCurrentPlatformQuery } from "@/partner/entities/platforms";
import { useFilter } from "@/partner/shared/components/Filter";
import { EmptyListCard } from "@/partner/shared/components/EmptyListCard";
import { PagePaths } from "@/partner/shared/constants";
import { useVideoProviderTextKeys } from "../model";
import { VideoListItem } from "./VideoListItem";
import { VideosToolbar } from "./VideosToolbar";
import { buildVideosFilter } from "../model/buildVideosFilter";

interface VideosListProps {
  onEditContent: (id: string) => void;
}

export const VideosList: FC<VideosListProps> = ({ onEditContent }) => {
  const { t } = useTranslation();
  const providerTextKeys = useVideoProviderTextKeys();
  const {
    onPageChange,
    onRowsPerPageChange,
    params: { page, rowsPerPage },
    filter,
  } = useFilter();

  const { data: platformData } = useCurrentPlatformQuery();
  const streamingAvailable = Boolean(
    platformData?.currentPlatform?.streamingAvailable
  );

  const { data, loading } = useContentsQuery({
    variables: {
      page,
      rowsPerPage,
      filter: {
        // Только видео: без type сюда попадал ЛЮБОЙ контент платформы,
        // включая стримы (streams/collections фильтруют так же).
        type: ContentTypeType.VideoType,
        ...buildVideosFilter(filter),
      },
    },
    fetchPolicy: "cache-and-network",
  });

  const contentsData = data?.contents;
  const contents = contentsData?.nodes ?? [];
  const count = contentsData?.count ?? 0;

  // collectionIds/productIds нужны AddToCollectionButton/AddToProductButton
  // для трёхсостояния чекбоксов «где уже лежит»/«к каким продуктам уже
  // привязано» (референсный contentToSelected видео)
  const [selectedItems, setSelectedItems] = useState<
    { id: string; collectionIds: string[]; productIds: string[] }[]
  >([]);
  const selectedIds = selectedItems.map((item) => item.id);

  const toSelected = (content: {
    id: string;
    collectionIds: string[];
    productIds: string[];
  }) => ({
    id: content.id,
    collectionIds: content.collectionIds,
    productIds: content.productIds,
  });

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelectedItems(contents.map(toSelected));
    } else {
      setSelectedItems([]);
    }
  };

  const handleSelectOne = (
    id: string,
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    event.stopPropagation();
    if (selectedIds.includes(id)) {
      setSelectedItems(selectedItems.filter((item) => item.id !== id));
    } else {
      const content = contents.find((item) => item.id === id);
      if (content) {
        setSelectedItems([...selectedItems, toSelected(content)]);
      }
    }
  };

  const numSelected = selectedIds.length;
  const indeterminate = numSelected > 0 && numSelected < contents.length;

  if (platformData?.currentPlatform && !streamingAvailable) {
    return (
      <Box sx={{ textAlign: "center", py: 6 }}>
        <Typography variant="h6">
          {t(providerTextKeys.listStreamNotConnectedTitle)}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t(providerTextKeys.listStreamNotConnectedDescription)}
        </Typography>
        <Button
          variant="contained"
          component={RouterLink}
          to={PagePaths.platformSettings}
        >
          {t("videos.list.goToSettingsButton")}
        </Button>
      </Box>
    );
  }

  if (loading && !data) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (contents.length === 0) {
    return <EmptyListCard title={t("videos.list.empty")} />;
  }

  return (
    <Box>
      <Card elevation={0}>
        <AppBar position="static" color="default" elevation={0}>
          <VideosToolbar
            indeterminate={indeterminate}
            numSelected={numSelected}
            nodes={contents}
            onSelectAllClick={handleSelectAll}
            selectedItems={selectedItems}
            onCloseSelected={() => setSelectedItems([])}
          />
        </AppBar>
      </Card>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mt: 1 }}>
        {contents.map((content) => (
          <VideoListItem
            key={content.id}
            content={content}
            selected={selectedIds.includes(content.id)}
            onSelectOneChange={handleSelectOne}
            onClickItem={onEditContent}
          />
        ))}
      </Box>

      <TablePagination
        component="div"
        count={count}
        page={page}
        onPageChange={onPageChange}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={onRowsPerPageChange}
        rowsPerPageOptions={[5, 10, 25, 100]}
        labelRowsPerPage={t("common.pagination.rowsPerPage")}
        labelDisplayedRows={({ from, to, count }) =>
          t("common.pagination.displayedRows", {
            from,
            to,
            count:
              count !== -1 ? count : t("common.pagination.moreThan", { to }),
          })
        }
      />
    </Box>
  );
};
